import React, { useCallback, useState, useEffect } from 'react';
import { getSdk } from '~/api';
import { GQLClient, GQLClientHeader } from '~/config/gql-client';
import { SellerContext } from './context';
import { useTranslate } from '@refinedev/core';
import { currentSellerId, authStorageKey, currentChannelToken } from '~/config/auth-provider';

// Type for channel data in seller context
export type SellerChannel = {
  id: string;
  code: string;
  token: string;
  name?: string | null;
  icon?: string | null;
  description?: string | null;
};

// Type for the seller data we use in the context
export type SellerData = {
  id: string;
  name: string;
  logo?: string | null;
  resourceConfig?: any;
  channels?: SellerChannel[];
};

export const SellerContextProvider = React.memo((props: any) => {
  const { children } = props;

  // ===================== HOOKS
  const translate = useTranslate();

  // ===================== STATES
  const [loading, setLoading] = useState(true);
  const [seller, setSeller] = useState<SellerData | null>(null);
  const [error, setError] = useState<string | undefined>();

  // ===================== VARIABLES
  const client = GQLClient.getInstance();

  // ===================== EVENTS
  const fetchSeller = useCallback(async () => {
    setLoading(true);
    const sellerId = localStorage.getItem(currentSellerId);
    const authToken = localStorage.getItem(authStorageKey);
    const channelToken = localStorage.getItem(currentChannelToken);

    if (!sellerId) {
      console.error(`No seller found`);
      setLoading(false);
      return;
    }

    if (!authToken) {
      console.error(`No auth token found`);
      setLoading(false);
      return;
    }

    // Set headers before making GraphQL requests
    // This is critical when reconnecting to production with existing tokens
    client.setHeader(GQLClientHeader.authorization, `Bearer ${authToken}`);
    if (channelToken) {
      client.setHeader(GQLClientHeader.vendureToken, channelToken);
    }

    try {
      // Fetch seller and channels in parallel
      const [sellerData, channelsData] = await Promise.all([
        getSdk(client).seller({ id: sellerId }),
        getSdk(client).sellerChannels(),
      ]);

      if (sellerData?.seller) {
        const rawSeller = sellerData.seller;

        // Extract logo from brandIdentity or fall back to legacy logo field
        let logo = rawSeller.customFields?.logo;
        if (rawSeller.customFields?.brandIdentity) {
          try {
            const brandIdentity = typeof rawSeller.customFields.brandIdentity === 'string'
              ? JSON.parse(rawSeller.customFields.brandIdentity)
              : rawSeller.customFields.brandIdentity;
            logo = brandIdentity?.logo || logo;
          } catch (e) {
            console.warn('Failed to parse brandIdentity:', e);
          }
        }

        // Parse resources from customFields
        let resourceConfig = [];
        if (rawSeller.customFields?.resources) {
          try {
            resourceConfig = typeof rawSeller.customFields.resources === 'string'
              ? JSON.parse(rawSeller.customFields.resources)
              : rawSeller.customFields.resources;
          } catch (e) {
            console.warn('Failed to parse resources:', e);
          }
        }

        // Map channels - filter by seller ID on client side
        const channels: SellerChannel[] = (channelsData?.channels?.items || [])
          .filter((ch) => ch.seller?.id === sellerId)
          .map((ch) => ({
            id: ch.id,
            code: ch.code,
            token: ch.token,
            // Use code as name fallback since customFields doesn't have name
            name: ch.code,
            icon: null,
            description: null,
          }));

        // Map to expected structure
        const mappedSeller: SellerData = {
          id: rawSeller.id,
          name: rawSeller.name,
          logo,
          resourceConfig,
          channels,
        };

        setSeller(mappedSeller);
      } else {
        setError('Seller not found');
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [client]);

  /**
   * Note:
   * This might be null, should use `loading` together
   * if it's initial fetch
   */
  const getCurrentSeller = useCallback(() => {
    if (!seller) fetchSeller();
    return seller;
  }, [seller, fetchSeller]);

  const getResourceConfig = () => {
    return seller?.resourceConfig || [];
  };

  // ===================== EFFECTS
  useEffect(() => {
    if (!seller) {
      fetchSeller();
    }
  }, [fetchSeller, seller]);

  // ===================== VIEWS
  return (
    <SellerContext.Provider
      value={{
        loading,
        error,
        getCurrentSeller,
        getResourceConfig,
        refetchSeller: fetchSeller,
      }}
    >
      {children}
    </SellerContext.Provider>
  );
});
